// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 5fdc224f-961a-42fa-8333-bfbc6fb2266d

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Alert, Stack, Divider, Link,
} from '@mui/material';
import PrimordiaLogo from './PrimordiaLogo';
import {
  login, completeNewPassword, completeMfaSetup, completeMfa,
  forgotPassword, confirmForgotPassword,
  beginSso, completeSsoRedirect, ssoAvailable, hasSsoRedirect, signOut,
} from './auth';

// ---------------------------------------------------------------------------
// QR encoding, done here rather than with a dependency.
//
// A QR library for one 45x45 grid is not worth the supply-chain surface on a
// sign-in page, so this is a byte-mode encoder at error-correction level M for
// versions 1-10 (up to 213 bytes — an otpauth:// URI is ~120). It was validated
// module-for-module against a reference implementation across every version and
// all eight masks before being committed.
//
// Two deliberate departures from that reference, neither of which changes what
// a scanner reads: padding follows ISO 18004 7.4.10 exactly (no extra zero
// codeword when the stream already ends on a byte boundary), and mask selection
// scores the finished symbol including its format bits.
// ---------------------------------------------------------------------------

const GF_EXP = new Array(512);
const GF_LOG = new Array(256).fill(0);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    GF_EXP[i] = x; GF_LOG[x] = i;
    x <<= 1; if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) GF_EXP[i] = GF_EXP[i - 255];
})();
const gfMul = (a, b) => ((!a || !b) ? 0 : GF_EXP[GF_LOG[a] + GF_LOG[b]]);

// [ecCodewordsPerBlock, blocksInGroup1, dataPerBlock1, blocksInGroup2, dataPerBlock2] at level M.
const QR_ECB = {
  1: [10, 1, 16, 0, 0],
  2: [16, 1, 28, 0, 0],
  3: [26, 1, 44, 0, 0],
  4: [18, 2, 32, 0, 0],
  5: [24, 2, 43, 0, 0],
  6: [16, 4, 27, 0, 0],
  7: [18, 4, 31, 0, 0],
  8: [22, 2, 38, 2, 39],
  9: [22, 3, 36, 2, 37],
  10: [26, 4, 43, 1, 44],
};
const QR_ALIGN = {
  1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30],
  6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
};
const QR_MASKS = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => ((((r * c) % 2) + ((r * c) % 3)) % 2) === 0,
  (r, c) => ((((r + c) % 2) + ((r * c) % 3)) % 2) === 0,
];

const qrDataCodewords = (v) => QR_ECB[v][1] * QR_ECB[v][2] + QR_ECB[v][3] * QR_ECB[v][4];

// Reed-Solomon remainder over GF(256), generator polynomial x^8+x^4+x^3+x^2+1.
function qrEcc(data, n) {
  let gen = [1];
  for (let i = 0; i < n; i += 1) {
    const next = new Array(gen.length + 1).fill(0);
    for (let j = 0; j < gen.length; j += 1) {
      next[j] ^= gen[j];
      next[j + 1] ^= gfMul(gen[j], GF_EXP[i]);
    }
    gen = next;
  }
  const res = new Array(n).fill(0);
  data.forEach((d) => {
    const factor = d ^ res[0];
    res.shift(); res.push(0);
    if (factor) for (let j = 0; j < n; j += 1) res[j] ^= gfMul(gen[j + 1], factor);
  });
  return res;
}

// Mode indicator + character count + data, terminated and padded to capacity.
function qrCodewords(bytes, version) {
  const total = qrDataCodewords(version);
  const bits = [];
  const push = (val, len) => { for (let i = len - 1; i >= 0; i -= 1) bits.push((val >> i) & 1); };
  push(0b0100, 4);
  push(bytes.length, version < 10 ? 8 : 16);
  bytes.forEach((b) => push(b, 8));
  for (let i = 0; i < 4 && bits.length < total * 8; i += 1) bits.push(0);
  while (bits.length % 8) bits.push(0);
  const out = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j += 1) b = (b << 1) | bits[i + j];
    out.push(b);
  }
  for (let i = 0; out.length < total; i += 1) out.push(i % 2 === 0 ? 0xec : 0x11);
  return out;
}

// Split into blocks, append each block's ECC, then interleave both.
function qrInterleave(bytes, version) {
  const [ec, b1, d1, b2, d2] = QR_ECB[version];
  const data = qrCodewords(bytes, version);
  const blocks = [];
  let pos = 0;
  for (let i = 0; i < b1; i += 1) { blocks.push(data.slice(pos, pos + d1)); pos += d1; }
  for (let i = 0; i < b2; i += 1) { blocks.push(data.slice(pos, pos + d2)); pos += d2; }
  const eccBlocks = blocks.map((b) => qrEcc(b, ec));
  const out = [];
  for (let i = 0; i < Math.max(d1, d2); i += 1) blocks.forEach((b) => { if (i < b.length) out.push(b[i]); });
  for (let i = 0; i < ec; i += 1) eccBlocks.forEach((b) => out.push(b[i]));
  return out;
}

// Function patterns: finders, separators, alignment, timing, dark module, and
// the reserved format/version regions (marked 2 so data placement skips them).
function qrSkeleton(version) {
  const n = 17 + 4 * version;
  const m = Array.from({ length: n }, () => new Array(n).fill(null));
  const set = (r, c, v) => { if (r >= 0 && r < n && c >= 0 && c < n) m[r][c] = v; };
  const finder = (r0, c0) => {
    for (let r = -1; r <= 7; r += 1) {
      for (let c = -1; c <= 7; c += 1) {
        const inner = r >= 0 && r <= 6 && c >= 0 && c <= 6;
        const on = inner && (r === 0 || r === 6 || c === 0 || c === 6
          || (r >= 2 && r <= 4 && c >= 2 && c <= 4));
        set(r0 + r, c0 + c, on ? 1 : 0);
      }
    }
  };
  finder(0, 0); finder(0, n - 7); finder(n - 7, 0);
  QR_ALIGN[version].forEach((r) => QR_ALIGN[version].forEach((c) => {
    if (m[r][c] !== null) return;
    for (let dr = -2; dr <= 2; dr += 1) {
      for (let dc = -2; dc <= 2; dc += 1) {
        set(r + dr, c + dc, Math.max(Math.abs(dr), Math.abs(dc)) === 1 ? 0 : 1);
      }
    }
  }));
  for (let i = 8; i < n - 8; i += 1) {
    const on = i % 2 === 0 ? 1 : 0;
    if (m[6][i] === null) m[6][i] = on;
    if (m[i][6] === null) m[i][6] = on;
  }
  m[n - 8][8] = 1;
  for (let i = 0; i < 9; i += 1) {
    if (m[8][i] === null) m[8][i] = 2;
    if (m[i][8] === null) m[i][8] = 2;
  }
  for (let i = 0; i < 8; i += 1) {
    if (m[8][n - 1 - i] === null) m[8][n - 1 - i] = 2;
    if (m[n - 1 - i][8] === null) m[n - 1 - i][8] = 2;
  }
  if (version >= 7) {
    for (let i = 0; i < 18; i += 1) {
      const r = Math.floor(i / 3);
      const c = n - 11 + (i % 3);
      m[r][c] = 2; m[c][r] = 2;
    }
  }
  return m;
}

// Two-module-wide columns, right to left, alternating up and down, skipping the
// vertical timing column.
function qrPlaceData(m, codewords) {
  const n = m.length;
  const bits = [];
  codewords.forEach((b) => { for (let i = 7; i >= 0; i -= 1) bits.push((b >> i) & 1); });
  let idx = 0;
  let upward = true;
  let col = n - 1;
  while (col > 0) {
    if (col === 6) col -= 1;
    for (let i = 0; i < n; i += 1) {
      const row = upward ? n - 1 - i : i;
      for (let k = 0; k < 2; k += 1) {
        const c = col - k;
        if (m[row][c] === null) {
          m[row][c] = idx < bits.length ? bits[idx] : 0;
          idx += 1;
        }
      }
    }
    upward = !upward;
    col -= 2;
  }
  return m;
}

function qrFormatBits(mask) {
  const v = (0b00 << 3) | mask; // 00 = error-correction level M
  let rem = v << 10;
  for (let i = 14; i >= 10; i -= 1) if ((rem >> i) & 1) rem ^= 0x537 << (i - 10);
  return ((v << 10) | rem) ^ 0x5412;
}

function qrVersionBits(version) {
  let rem = version << 12;
  for (let i = 17; i >= 12; i -= 1) if ((rem >> i) & 1) rem ^= 0x1f25 << (i - 12);
  return (version << 12) | rem;
}

function qrApplyFormat(m, mask, version) {
  const n = m.length;
  const f = qrFormatBits(mask);
  for (let i = 0; i < 15; i += 1) {
    const b = (f >> i) & 1;
    if (i < 6) m[i][8] = b;
    else if (i < 8) m[i + 1][8] = b;
    else m[n - 15 + i][8] = b;
    if (i < 8) m[8][n - i - 1] = b;
    else if (i === 8) m[8][15 - i] = b;
    else m[8][15 - i - 1] = b;
  }
  m[n - 8][8] = 1;
  if (version >= 7) {
    const v = qrVersionBits(version);
    for (let i = 0; i < 18; i += 1) {
      const b = (v >> i) & 1;
      const r = Math.floor(i / 3);
      const c = n - 11 + (i % 3);
      m[r][c] = b; m[c][r] = b;
    }
  }
  return m;
}

// ISO 18004 7.8.3.1 penalty rules — lowest score wins.
function qrPenalty(m) {
  const n = m.length;
  let score = 0;
  const runs = (get) => {
    for (let a = 0; a < n; a += 1) {
      let count = 1;
      for (let b = 1; b < n; b += 1) {
        if (get(a, b) === get(a, b - 1)) count += 1;
        else { if (count >= 5) score += count - 2; count = 1; }
      }
      if (count >= 5) score += count - 2;
    }
  };
  runs((a, b) => m[a][b]);
  runs((a, b) => m[b][a]);
  for (let r = 0; r < n - 1; r += 1) {
    for (let c = 0; c < n - 1; c += 1) {
      const v = m[r][c];
      if (v === m[r][c + 1] && v === m[r + 1][c] && v === m[r + 1][c + 1]) score += 3;
    }
  }
  const pat = [1, 0, 1, 1, 1, 0, 1];
  const matchAt = (seq, i) => pat.every((p, k) => seq[i + k] === p);
  const light = (seq, from, to) => !seq.slice(Math.max(from, 0), Math.min(to, n)).some(Boolean);
  // The 1:1:3:1:1 finder-like run, scored when flanked by four light modules on
  // either side or when it sits against the edge of the symbol.
  const finderLike = (seq) => {
    let count = 0;
    let i = 0;
    while (i + 7 <= n) {
      if (!matchAt(seq, i)) i += 1;
      else if (i === 0 || i === n - 7 || light(seq, i - 4, i) || light(seq, i + 7, i + 11)) {
        count += 40; i += 7;
      } else i += 4;
    }
    return count;
  };
  for (let a = 0; a < n; a += 1) {
    score += finderLike(m[a]);
    score += finderLike(m.map((row) => row[a]));
  }
  let dark = 0;
  m.forEach((row) => row.forEach((v) => { dark += v; }));
  score += Math.floor(Math.abs((dark * 100) / (n * n) - 50) / 5) * 10;
  return score;
}

// Returns a square matrix of 0/1 modules, or null if the text will not fit.
function qrEncode(text) {
  const bytes = Array.from(new TextEncoder().encode(text));
  let version = 0;
  for (let v = 1; v <= 10; v += 1) {
    if (qrDataCodewords(v) - (v < 10 ? 2 : 3) >= bytes.length) { version = v; break; }
  }
  if (!version) return null;
  const base = qrSkeleton(version);
  const reserved = base.map((row) => row.map((v) => v !== null));
  qrPlaceData(base, qrInterleave(bytes, version));
  let best = null;
  [0, 1, 2, 3, 4, 5, 6, 7].forEach((mask) => {
    const m = base.map((row, r) => row.map((v, c) => {
      if (reserved[r][c]) return v === 2 ? 0 : v;
      return QR_MASKS[mask](r, c) ? v ^ 1 : v;
    }));
    qrApplyFormat(m, mask, version);
    const p = qrPenalty(m);
    if (!best || p < best.p) best = { p, m };
  });
  return best.m;
}

// Never let a drawing problem block enrolment: on any failure the caller shows
// the setup key to be typed in by hand instead.
function safeQrEncode(text) {
  try {
    return qrEncode(text);
  } catch (e) {
    return null;
  }
}

// Renders the matrix as SVG, one rect per horizontal run of dark modules.
function QrCode({ matrix, size = 176 }) {
  const quiet = 4;
  const dim = matrix.length + quiet * 2;
  const rects = [];
  matrix.forEach((row, r) => {
    let start = -1;
    for (let c = 0; c <= row.length; c += 1) {
      const dark = c < row.length && row[c] === 1;
      if (dark && start < 0) start = c;
      if (!dark && start >= 0) {
        rects.push(<rect key={`${r}-${start}`} x={start + quiet} y={r + quiet} width={c - start} height={1} fill="#021F4B" />);
        start = -1;
      }
    }
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${dim} ${dim}`} shapeRendering="crispEdges"
      role="img" aria-label="QR code containing your authenticator setup key">
      <rect x="0" y="0" width={dim} height={dim} fill="#ffffff" />
      {rects}
    </svg>
  );
}

// Brand block, shared by the sign-in card and the SSO interstitials.
function Brand() {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
      <PrimordiaLogo size={44} />
      <Box sx={{ lineHeight: 1 }}>
        <Typography sx={{ fontWeight: 800, color: '#021F4B', fontSize: 24, lineHeight: 1 }}>
          Primordi<span style={{ color: '#0D5B9A' }}>a</span>
        </Typography>
        <Typography sx={{ fontSize: 8.5, color: '#5C6B7A', letterSpacing: 1.4, fontWeight: 700 }}>
          WHERE ENTERPRISE SYSTEMS BEGIN
        </Typography>
        <Typography sx={{ fontSize: 8, color: '#9AA6B2', fontWeight: 600 }}>by Optimum Healthcare IT</Typography>
      </Box>
    </Stack>
  );
}

function Shell({ children }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper sx={{ p: 4, width: 400, maxWidth: '100%' }} elevation={3}>{children}</Paper>
    </Box>
  );
}

// Cognito reports the same handful of MFA failures over and over, and the
// difference between them is the difference between "try again" and "you are
// stuck". Say which one it is.
const SESSION_EXPIRED = 'Your sign-in timed out before it finished. Nothing was lost — '
  + 'please sign in again to start over.';

function mfaErrorMessage(e, fallback) {
  const name = (e && e.name) || '';
  switch (name) {
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return 'That code is not correct. Check that you are reading the Primordia entry in your '
        + 'authenticator app, and enter the six digits it shows right now.';
    case 'ExpiredCodeException':
      return 'That code has already expired. Authenticator codes change every 30 seconds — '
        + 'wait for the next one to appear and enter it straight away.';
    case 'NotAuthorizedException':
      // Cognito uses this for both an expired challenge session and too many
      // failed attempts. Either way the only way forward is a fresh sign-in.
      return SESSION_EXPIRED;
    case 'TooManyFailedAttemptsException':
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Wait a minute, then sign in again.';
    case 'SoftwareTokenMFANotFoundException':
      return 'This account has no authenticator registered. Ask an administrator to reset your '
        + 'multi-factor setup, then sign in again.';
    default:
      return (e && e.message) || fallback;
  }
}

// A failure that invalidated the Cognito session — the user cannot continue
// from where they are and must restart sign-in.
const isSessionDead = (e) => ['NotAuthorizedException', 'TooManyFailedAttemptsException',
  'LimitExceededException', 'TooManyRequestsException'].includes((e && e.name) || '');

// ---------------------------------------------------------------------------
// Self-service password reset.
//
// Deliberately says the same thing whether or not the address exists. Cognito
// throws UserNotFoundException, which would otherwise turn this form into an
// account-enumeration oracle for a product whose whole premise is that clients
// cannot see each other — knowing WHO has an account is already a leak.
// ---------------------------------------------------------------------------

const RESET_SENT = 'If an account exists for that address, a six-digit code is on its way. '
  + 'It can take a minute or two to arrive, and it expires after an hour.';

// A user who has never completed a first sign-in has no password to recover, and
// Cognito refuses the whole flow for them. That is the single most likely reason
// this form fails for a real person, and "reset failed" tells them nothing —
// they need to know that only an administrator can move them forward.
const RESET_NEEDS_ADMIN = 'This account has not finished its first sign-in yet, so it cannot be '
  + 'reset from here. Ask an Optimum administrator to issue you a new temporary password.';

function requestResetMessage(e) {
  switch ((e && e.name) || '') {
    case 'UserNotFoundException':
      return null; // never disclosed — caller proceeds to the code step regardless
    case 'NotAuthorizedException':
      // Cognito's answer for a FORCE_CHANGE_PASSWORD or disabled account.
      return RESET_NEEDS_ADMIN;
    case 'InvalidParameterException':
      // Raised when the account has no verified email to send a code to.
      return 'This account has no verified email address, so a code cannot be sent. '
        + 'Please contact an Optimum administrator.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many reset attempts. Wait a few minutes before trying again.';
    default:
      return (e && e.message) || 'Could not start a password reset.';
  }
}

function confirmResetMessage(e) {
  switch ((e && e.name) || '') {
    case 'CodeMismatchException':
      return 'That code is not correct. Check the most recent email — an earlier code stops '
        + 'working as soon as a new one is sent.';
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one and use it within the hour.';
    case 'InvalidPasswordException':
      return 'That password does not meet the requirements: at least 8 characters, with an '
        + 'uppercase letter, a lowercase letter, and a number.';
    case 'UserNotFoundException':
      // Only reachable when the address never existed — the request step said
      // nothing, so this step must not say anything either.
      return 'That code is not correct.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Wait a few minutes before trying again.';
    default:
      return (e && e.message) || 'Could not reset your password.';
  }
}

// Gated entry. Primordia is never open to the world — every deployed route sits
// behind this, and the API independently rejects unauthenticated calls.
export default function Login({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [challenge, setChallenge] = useState(null); // { session, email }
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  // Password reset: null when off, otherwise 'request' (ask for the address) or
  // 'confirm' (code + new password). Its own code state, kept apart from the MFA
  // code so neither step can inherit digits typed into the other.
  const [reset, setReset] = useState(null);
  const [resetCode, setResetCode] = useState('');

  // TOTP enrolment. The shared secret lives HERE and nowhere else — never in
  // localStorage, never in sessionStorage, never in a URL — and is dropped the
  // moment setup finishes.
  const [mfaSetup, setMfaSetup] = useState(null); // { session, email, username, secretCode, otpauthUri }
  const [showSecret, setShowSecret] = useState(false);
  const [verifying, setVerifying] = useState(false); // on the code step of setup
  const [mfa, setMfa] = useState(null); // { session, email, username }
  const [code, setCode] = useState('');

  // Drawn from the secret, so it is recomputed only while enrolment is live.
  const qrMatrix = useMemo(
    () => (mfaSetup && mfaSetup.otpauthUri ? safeQrEncode(mfaSetup.otpauthUri) : null),
    [mfaSetup],
  );

  // Single sign-on state. `exchanging` starts true when we were redirected back
  // from the hosted UI, so the password form never flashes mid-exchange.
  const [exchanging, setExchanging] = useState(() => hasSsoRedirect());
  const [ssoBusy, setSsoBusy] = useState(false);
  const [ssoErr, setSsoErr] = useState('');
  const [ssoNotice, setSsoNotice] = useState('');
  const [unassigned, setUnassigned] = useState(false);

  // No hosted-UI domain in runtime config means there is nothing to redirect to,
  // so we hide the button entirely rather than offer one that cannot work.
  const showSso = ssoAvailable();

  // The parent's callback identity is not a reason to re-run the one-shot
  // redirect exchange, so it is read through a ref.
  const signedInRef = useRef(onSignedIn);
  useEffect(() => { signedInRef.current = onSignedIn; }, [onSignedIn]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await completeSsoRedirect();
      if (!alive) return;
      if (!res || res.status === 'none') { setExchanging(false); return; }
      if (res.status === 'not-configured') {
        setSsoNotice(res.message);
        setExchanging(false);
        return;
      }
      if (res.status === 'error') {
        setSsoErr(res.message);
        setExchanging(false);
        return;
      }
      // Signed in. A federated user with no tenant and no group is entitled to
      // nothing, so they are held here with an explanation instead of being
      // dropped into an empty workspace — and we do NOT assign one for them.
      if (res.needsAssignment) {
        setUnassigned(true);
        setExchanging(false);
        return;
      }
      setExchanging(false);
      signedInRef.current(res.session);
    })();
    return () => { alive = false; };
  }, []);

  const startSso = async () => {
    setErr(''); setSsoErr(''); setSsoNotice(''); setSsoBusy(true);
    try {
      await beginSso(); // navigates away on success
    } catch (e2) {
      setSsoErr(e2.message || 'Could not start single sign-on.');
      setSsoBusy(false);
    }
  };

  // Back to a clean password form, with the secret and every spent session
  // dropped. Used after a dead session and by the "start over" links.
  const restart = (message) => {
    setChallenge(null); setMfaSetup(null); setMfa(null);
    setCode(''); setPassword(''); setNewPassword(''); setConfirmPassword('');
    setShowSecret(false); setVerifying(false);
    setReset(null); setResetCode(''); setNotice('');
    setErr(message || '');
  };

  // Cognito can hand back tokens or the next challenge in the chain; both the
  // password step and the new-password step funnel through here, which is what
  // makes NEW_PASSWORD_REQUIRED -> MFA_SETUP work in a single sign-in.
  const applyResult = (res) => {
    if (res.ok) { onSignedIn(res.session); return; }
    setCode('');
    if (res.challenge === 'NEW_PASSWORD_REQUIRED') {
      setChallenge(res);
      return;
    }
    if (res.challenge === 'MFA_SETUP') {
      setChallenge(null);
      setVerifying(false);
      setMfaSetup(res);
      return;
    }
    if (res.challenge === 'SOFTWARE_TOKEN_MFA') {
      setChallenge(null);
      setMfa(res);
      return;
    }
    setErr('Sign-in needs a step this app cannot complete. Please contact an administrator.');
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setNotice(''); setBusy(true);
    try {
      applyResult(await login(email.trim(), password));
    } catch (e2) {
      setErr(e2.name === 'NotAuthorizedException' ? 'Incorrect email or password.' : (e2.message || 'Sign-in failed'));
    } finally { setBusy(false); }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setErr('');
    if (newPassword !== confirmPassword) { setErr('Passwords do not match.'); return; }
    setBusy(true);
    try {
      applyResult(await completeNewPassword(challenge.email, newPassword, challenge.session, challenge.username));
    } catch (e2) {
      setErr(e2.message || 'Could not set password');
    } finally { setBusy(false); }
  };

  // Ask Cognito to email a code. A missing account is NOT reported: we advance to
  // the code step either way, so the form cannot be used to test who has one.
  const requestReset = async (e) => {
    if (e) e.preventDefault();
    setErr(''); setNotice(''); setBusy(true);
    try {
      await forgotPassword(email.trim());
      setReset('confirm');
      setNotice(RESET_SENT);
    } catch (e2) {
      const message = requestResetMessage(e2);
      if (message) setErr(message);
      else { setReset('confirm'); setNotice(RESET_SENT); }
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setErr('');
    if (newPassword !== confirmPassword) { setErr('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await confirmForgotPassword(email.trim(), resetCode.trim(), newPassword);
      // Deliberately does not sign them in. The password is only the first
      // factor; sending them back through the form means MFA is enforced the
      // same way it is on every other sign-in.
      setReset(null); setResetCode(''); setNewPassword(''); setConfirmPassword(''); setPassword('');
      setNotice('Your password has been changed. Sign in with it below.');
    } catch (e2) {
      setErr(confirmResetMessage(e2));
    } finally { setBusy(false); }
  };

  // One code form serves both first-time verification and routine sign-in.
  const submitCode = async (e) => {
    e.preventDefault();
    // Enter can fire the form while the button is still disabled.
    if (code.length !== 6) { setErr('Enter all six digits of the code.'); return; }
    setErr(''); setBusy(true);
    try {
      const res = mfaSetup ? await completeMfaSetup(code, mfaSetup) : await completeMfa(code, mfa);
      // Enrolment is done: forget the secret before anything else happens.
      setMfaSetup(null); setShowSecret(false); setVerifying(false); setCode('');
      applyResult(res);
    } catch (e2) {
      if (isSessionDead(e2)) restart(mfaErrorMessage(e2, SESSION_EXPIRED));
      else { setErr(mfaErrorMessage(e2, 'Could not verify that code.')); setCode(''); }
    } finally { setBusy(false); }
  };

  if (exchanging) {
    return (
      <Shell>
        <Brand />
        <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
          <PrimordiaLogo size={64} spinning />
          <Typography sx={{ fontSize: 11, color: '#5C6B7A', letterSpacing: 1.5, fontWeight: 700 }}>
            SIGNING YOU IN
          </Typography>
        </Stack>
      </Shell>
    );
  }

  if (unassigned) {
    return (
      <Shell>
        <Brand />
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>Your account needs to be set up</Typography>
          You signed in successfully, but your account has no workspace or role assigned yet.
          An Optimum administrator must assign your workspace and role before you can see anything
          in Primordia. Please contact your administrator and try again once that is done.
        </Alert>
        <Button fullWidth variant="outlined" onClick={() => signOut()}
          sx={{ color: '#021F4B', borderColor: '#021F4B', py: 1.1 }}>
          Sign out
        </Button>
      </Shell>
    );
  }

  const inChallenge = !!challenge || !!mfaSetup || !!mfa;
  // The reset flow replaces the sign-in form rather than sitting beside it, so
  // it hides the SSO block and the password fields too.
  const signingIn = !inChallenge && !reset;
  const enrolling = !!mfaSetup && verifying;
  const secret = mfaSetup ? mfaSetup.secretCode : '';
  const grouped = secret.replace(/(.{4})/g, '$1 ').trim();

  // The enrolment screen: scan, or type the key in by hand.
  const setupStep = mfaSetup && !verifying && (
    <Box>
      <Typography sx={{ fontWeight: 700, color: '#021F4B', fontSize: 16, mb: 0.5 }}>
        Set up two-step verification
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 2 }}>
        Primordia now requires a code from an authenticator app (Microsoft Authenticator, Google
        Authenticator, 1Password, or similar) each time you sign in. Scan this once to add it.
      </Typography>

      {qrMatrix ? (
        <>
          <Stack alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ p: 1.25, bgcolor: '#fff', border: '1px solid #E2E7EC', borderRadius: 1, lineHeight: 0 }}>
              <QrCode matrix={qrMatrix} />
            </Box>
          </Stack>
          <Typography sx={{ fontSize: 12, color: '#5C6B7A', mb: 1 }}>
            Cannot scan it?
            {' '}
            <Link component="button" type="button" underline="hover" onClick={() => setShowSecret(true)}
              sx={{ fontSize: 12, color: '#0D5B9A', fontWeight: 700, verticalAlign: 'baseline' }}>
              Enter the key by hand
            </Link>
            .
          </Typography>
        </>
      ) : (
        <Alert severity="info" sx={{ mb: 2, fontSize: 12.5 }}>
          No QR code is shown here — this browser could not draw one. Add the account by hand
          using the setup key below; it works exactly the same.
        </Alert>
      )}

      {(showSecret || !qrMatrix) && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#F4F7FA', border: '1px solid #E2E7EC', borderRadius: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#5C6B7A', letterSpacing: 0.8, mb: 0.75 }}>
            SETUP KEY
          </Typography>
          <Typography component="div" sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: 15, fontWeight: 700, color: '#021F4B', letterSpacing: 1.2,
            wordBreak: 'break-all', userSelect: 'all', mb: 0.75,
          }}>
            {grouped}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: '#5C6B7A' }}>
            In your authenticator app choose &ldquo;add account&rdquo; then &ldquo;enter a setup key
            manually&rdquo;, name it Primordia, and type the key above (spaces do not matter). This
            key is shown only during setup and never again.
          </Typography>
        </Box>
      )}

      <Button fullWidth variant="contained" onClick={() => { setErr(''); setVerifying(true); }}
        sx={{ bgcolor: '#0D5B9A', py: 1.2, '&:hover': { bgcolor: '#021F4B' } }}>
        I have added it — continue
      </Button>
      <Button fullWidth variant="text" onClick={() => restart()}
        sx={{ color: '#5C6B7A', fontSize: 12, mt: 0.5 }}>
        Cancel and start over
      </Button>
    </Box>
  );

  // The code step, shared by first-time verification and every later sign-in.
  const codeStep = (mfa || enrolling) && (
    <form onSubmit={submitCode}>
      <Typography sx={{ fontWeight: 700, color: '#021F4B', fontSize: 16, mb: 0.5 }}>
        {enrolling ? 'Confirm your authenticator' : 'Two-step verification'}
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 2 }}>
        {enrolling
          ? 'Enter the six-digit code your authenticator app is showing for Primordia to finish setup.'
          : 'Enter the six-digit code from your authenticator app.'}
      </Typography>
      <TextField fullWidth label="6-digit code" value={code} autoFocus size="small" required
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        inputProps={{
          inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, autoComplete: 'one-time-code',
          style: { letterSpacing: 6, fontSize: 20, fontWeight: 700, textAlign: 'center', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
        }}
        helperText="Codes change every 30 seconds." sx={{ mb: 2.5 }} />
      <Button fullWidth type="submit" variant="contained" disabled={busy || code.length !== 6}
        sx={{ bgcolor: '#0D5B9A', py: 1.2, '&:hover': { bgcolor: '#021F4B' } }}>
        {busy ? <PrimordiaLogo size={26} spinning /> : (enrolling ? 'Verify & finish setup' : 'Verify')}
      </Button>
      <Button fullWidth variant="text"
        onClick={() => { if (enrolling) { setErr(''); setCode(''); setVerifying(false); } else restart(); }}
        sx={{ color: '#5C6B7A', fontSize: 12, mt: 0.5 }}>
        {enrolling ? 'Back to the setup key' : 'Cancel and start over'}
      </Button>
    </form>
  );

  return (
    <Shell>
      <Brand />

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {notice && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}
      {ssoErr && <Alert severity="error" sx={{ mb: 2 }}>{ssoErr}</Alert>}
      {ssoNotice && <Alert severity="info" sx={{ mb: 2 }}>{ssoNotice}</Alert>}

      {signingIn && showSso && (
        <>
          <Button fullWidth variant="contained" onClick={startSso} disabled={ssoBusy}
            sx={{ bgcolor: '#021F4B', py: 1.35, fontWeight: 700, '&:hover': { bgcolor: '#0D5B9A' } }}>
            {ssoBusy ? <PrimordiaLogo size={26} spinning /> : 'Sign in with Optimum'}
          </Button>
          <Typography sx={{ fontSize: 11, color: '#5C6B7A', mt: 1, textAlign: 'center' }}>
            Use your Optimum Healthcare IT work account.
          </Typography>
          <Divider sx={{ my: 2.5, fontSize: 11, color: '#9AA6B2' }}>OR</Divider>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#5C6B7A', mb: 1.5 }}>
            Sign in with a Primordia account
          </Typography>
        </>
      )}

      {setupStep}
      {codeStep}

      {signingIn && (
        <form onSubmit={submit}>
          <TextField fullWidth label="Email" type="email" value={email} autoFocus
            onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} size="small" required />
          <TextField fullWidth label="Password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} sx={{ mb: 3 }} size="small" required />
          <Button fullWidth type="submit" variant={showSso ? 'outlined' : 'contained'} disabled={busy}
            sx={showSso
              ? { color: '#0D5B9A', borderColor: '#0D5B9A', py: 1.2, '&:hover': { borderColor: '#021F4B', color: '#021F4B' } }
              : { bgcolor: '#0D5B9A', py: 1.2, '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? <PrimordiaLogo size={26} spinning /> : 'Sign in'}
          </Button>
          <Box sx={{ textAlign: 'center', mt: 1.5 }}>
            <Link component="button" type="button" underline="hover" disabled={busy}
              onClick={() => { setErr(''); setNotice(''); setPassword(''); setReset('request'); }}
              sx={{ fontSize: 12.5, color: '#0D5B9A', fontWeight: 700 }}>
              Forgot your password?
            </Link>
          </Box>
        </form>
      )}

      {reset === 'request' && (
        <form onSubmit={requestReset}>
          <Typography sx={{ fontWeight: 700, color: '#021F4B', fontSize: 16, mb: 0.5 }}>
            Reset your password
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 2 }}>
            Enter the email address you sign in with and we will send you a six-digit code to
            set a new password.
          </Typography>
          <TextField fullWidth label="Email" type="email" value={email} autoFocus required size="small"
            onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2.5 }} />
          <Button fullWidth type="submit" variant="contained" disabled={busy || !email.trim()}
            sx={{ bgcolor: '#0D5B9A', py: 1.2, '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? <PrimordiaLogo size={26} spinning /> : 'Send code'}
          </Button>
          <Button fullWidth variant="text" onClick={() => restart()}
            sx={{ color: '#5C6B7A', fontSize: 12, mt: 0.5 }}>
            Back to sign in
          </Button>
        </form>
      )}

      {reset === 'confirm' && (
        <form onSubmit={submitReset}>
          <Typography sx={{ fontWeight: 700, color: '#021F4B', fontSize: 16, mb: 0.5 }}>
            Set a new password
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 2 }}>
            Enter the code sent to
            {' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#021F4B' }}>{email.trim()}</Box>
            {' '}
            and choose a new password.
          </Typography>
          <TextField fullWidth label="6-digit code" value={resetCode} autoFocus size="small" required
            onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{
              inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, autoComplete: 'one-time-code',
              style: { letterSpacing: 6, fontSize: 20, fontWeight: 700, textAlign: 'center', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
            }}
            sx={{ mb: 2 }} />
          <TextField fullWidth label="New password" type="password" value={newPassword} required size="small"
            onChange={(e) => setNewPassword(e.target.value)} sx={{ mb: 2 }}
            inputProps={{ autoComplete: 'new-password' }}
            helperText="8+ characters, with upper, lower, and a number" />
          <TextField fullWidth label="Confirm password" type="password" value={confirmPassword} required size="small"
            onChange={(e) => setConfirmPassword(e.target.value)} sx={{ mb: 2.5 }}
            inputProps={{ autoComplete: 'new-password' }} />
          <Button fullWidth type="submit" variant="contained"
            disabled={busy || resetCode.length !== 6 || !newPassword || !confirmPassword}
            sx={{ bgcolor: '#0D5B9A', py: 1.2, '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? <PrimordiaLogo size={26} spinning /> : 'Set new password'}
          </Button>
          <Button fullWidth variant="text" disabled={busy} onClick={requestReset}
            sx={{ color: '#5C6B7A', fontSize: 12, mt: 0.5 }}>
            Send a new code
          </Button>
          <Button fullWidth variant="text" onClick={() => restart()}
            sx={{ color: '#5C6B7A', fontSize: 12 }}>
            Back to sign in
          </Button>
        </form>
      )}

      {challenge && (
        <form onSubmit={submitNewPassword}>
          <Alert severity="info" sx={{ mb: 2 }}>Set a permanent password to continue.</Alert>
          <TextField fullWidth label="New password" type="password" value={newPassword} autoFocus
            onChange={(e) => setNewPassword(e.target.value)} sx={{ mb: 2 }} size="small" required
            helperText="8+ characters, with upper, lower, and a number" />
          <TextField fullWidth label="Confirm password" type="password" value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)} sx={{ mb: 3 }} size="small" required />
          <Button fullWidth type="submit" variant="contained" disabled={busy}
            sx={{ bgcolor: '#0D5B9A', py: 1.2, '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? <PrimordiaLogo size={26} spinning /> : 'Set password & continue'}
          </Button>
        </form>
      )}
    </Shell>
  );
}
